import { ProgrammingPercentage } from '@domain/builders';
import { ItemValues } from '@domain/entities';
import { ISummaryDataTable } from '@domain/interfaces';
import { LongDateTableItem } from '../../domain/entities';
import { toTitleCase } from '@domain/helpers';

export class LongDateTableSummary implements ISummaryDataTable {
  // eslint-disable-next-line no-empty-function
  constructor(readonly data: ProgrammingPercentage[]) {}

  get hasData() {
    return !!this.data?.length;
  }

  listDates() {
    const data = this.data?.reduce((prev, curr) => {
      const date = curr.dateLabel();
      (prev as Set<string>).add(date);

      return prev;
    }, new Set<string>());

    return [...data];
  }

  totals() {
    return this.data.reduce(
      (prev, total) => ({
        ...prev,
        [total.dateLabel()]: total.totals(),
      }),
      {} as Record<string, ItemValues>,
    );
  }

  percentages() {
    return this.data.reduce(
      (prev, total) => ({
        ...prev,
        [total.dateLabel()]: total.percentages(),
      }),
      {} as Record<string, ItemValues>,
    );
  }

  dataRows(): LongDateTableItem[] {
    const mapTable = new Map<string, LongDateTableItem>();
    const mapSubTable = new Map<string, LongDateTableItem>();

    this.data.forEach((filial) => {
      filial.timeFrames.forEach((tf) => {
        const timeFrameId = tf.timeFrameId;
        const dateLabel = filial.dateLabel();
        const totals = tf.totals();

        if (!mapTable.has(timeFrameId)) {
          mapTable.set(
            timeFrameId,
            LongDateTableItem.fromJson({
              id: tf.timeFrameId,
              label: toTitleCase(tf.timeFrameSlotName),
              hasToggle: true,
              values: new Map([[dateLabel, totals]]),
            }),
          );
        } else {
          mapTable.get(timeFrameId).values.set(dateLabel, totals);
        }

        tf.bags.forEach((bag) => {
          const bagKey = `${timeFrameId}.${bag.bagId}`;
          const subTotals = bag.totals();

          if (!mapSubTable.has(bagKey)) {
            mapSubTable.set(
              bagKey,
              LongDateTableItem.fromJson({
                id: bagKey,
                label: bag.label,
                hasToggle: false,
                nestedLevel: 1,
                parentId: timeFrameId,
                values: new Map([[dateLabel, subTotals]]),
              }),
            );
          } else {
            mapSubTable.get(bagKey).values.set(dateLabel, subTotals);
          }
        });
      });
    });

    const subItems = [...mapSubTable.values()];

    const finalList = [...mapTable.values()].reduce((prev, curr) => {
      const item = subItems?.filter((subi) => subi.parentId === curr.id) || [];

      return [...prev, curr, ...item];
    }, []);

    return finalList;
  }

  dataPercentageRows(): LongDateTableItem[] {
    const mapTable = new Map<string, LongDateTableItem>();
    const mapSubTable = new Map<string, LongDateTableItem>();

    this.data.forEach((filial) => {
      filial.timeFrames.forEach((tf) => {
        const timeFrameId = tf.timeFrameId;
        const dateLabel = filial.dateLabel();
        const percentages = tf.percentages();

        if (!mapTable.has(timeFrameId)) {
          mapTable.set(
            timeFrameId,
            LongDateTableItem.fromJson({
              id: tf.timeFrameId,
              label: toTitleCase(tf.timeFrameSlotName),
              hasToggle: true,
              values: new Map([[dateLabel, percentages]]),
            }),
          );
        } else {
          mapTable.get(timeFrameId).values.set(dateLabel, percentages);
        }

        tf.bags.forEach((bag) => {
          const bagKey = `${timeFrameId}.${bag.bagId}`;
          const subPercentageTotals = bag.percentages();

          if (!mapSubTable.has(bagKey)) {
            mapSubTable.set(
              bagKey,
              LongDateTableItem.fromJson({
                id: bagKey,
                label: bag.label,
                hasToggle: false,
                nestedLevel: 1,
                parentId: timeFrameId,
                values: new Map([[dateLabel, subPercentageTotals]]),
              }),
            );
          } else {
            mapSubTable.get(bagKey).values.set(dateLabel, subPercentageTotals);
          }
        });
      });
    });

    const subItems = [...mapSubTable.values()];

    const finalList = [...mapTable.values()].reduce((prev, curr) => {
      const item = subItems?.filter((subi) => subi.parentId === curr.id) || [];

      return [...prev, curr, ...item];
    }, []);

    return finalList;
  }
}
